import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react'
import { v4 as uuid } from 'uuid'
import {
  Note,
  NoteType,
  getNoteExtension,
  readNoteTitleFromFrontmatter,
} from '../utils/note'
import { useCreateNote } from '../hooks/useCreateNote'
import { useSaveNote } from '../hooks/useSaveNote'
import { fetchNote, useNoteConfig } from '../hooks/useNote'
import { usePrivateNotesExtension } from '../hooks/usePrivateNotesExtension'

import { useNotification } from './NotificationProvider'
import { trackError } from '../lib/analytics'
import { errorIcon } from '../components/Notification'
import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { noteQueryKey } from '../utils/queryKeyFactory'
import { cachedNotesContext } from '../contexts/CachedNotes'
import { useAuthenticatedUser } from '../providers/UserProvider'

const BATCH_TIMEOUT = 10_000

// #region Type Definitions

export type OperationInput = {
  dateString: string
}

export type AddContentInput = OperationInput & {
  type: 'add'
  content: string
}

export type RemoveContentInput = OperationInput & {
  type: 'remove'
  pattern: string | string[]
}

export type CreateFolderInput = {
  type: 'createFolder'
  filename: string
  title?: string
  noteType: NoteType
  parent: string | null
}

export type OperationStatus = 'pending' | 'processing' | 'completed' | 'error'

export type Operation = {
  id: string
  dateString?: string
  type: 'add' | 'remove' | 'createFolder'
  status: OperationStatus
  errorMessage?: string
  content?: string
  pattern?: string | string[]
  filename?: string
  title?: string
  noteType?: NoteType
  parent?: string | null
}

export type BatchStatus = 'queued' | 'processing' | 'completed' | 'error'

export type Batch = {
  id: string
  status: BatchStatus
  operations: Operation[]
  notificationSent?: boolean
  description?: string
  teamspaceId?: string
}

export type Queue = {
  batches: Batch[]
  isProcessing: boolean
}

export type NoteProcessorContextType = {
  addContent: (
    items: AddContentInput[],
    description?: string,
    batchId?: string,
    teamspaceId?: string
  ) => string | undefined
  removeContent: (
    items: RemoveContentInput[],
    description?: string,
    teamspaceId?: string
  ) => string | undefined
  createFolder: (
    items: CreateFolderInput[],
    description?: string
  ) => string | undefined
}

export const NoteProcessorContext = createContext<
  NoteProcessorContextType | undefined
>(undefined)
// #endregion

// #region Helper functions
function createBatch(
  operations: Operation[],
  description?: string,
  customBatchId?: string,
  teamspaceId?: string
): Batch {
  const batchId = customBatchId ?? uuid()

  return {
    id: batchId,
    status: 'queued',
    operations: operations.map((op) => ({
      ...op,
      status: 'pending',
    })),
    description,
    notificationSent: false,
    teamspaceId,
  }
}

function createOperations(
  items: (AddContentInput | RemoveContentInput)[]
): Operation[] {
  return items.map((item) => ({
    id: uuid(),
    dateString: item.dateString,
    type: item.type,
    status: 'pending',
    ...(item.type === 'add'
      ? { content: item.content }
      : { pattern: item.pattern }),
  }))
}

function createFolderOperations(items: CreateFolderInput[]): Operation[] {
  return items.map((item) => ({
    id: uuid(),
    type: item.type,
    status: 'pending',
    filename: item.filename,
    title: item.title,
    noteType: item.noteType,
    parent: item.parent,
  }))
}
// #endregion

type NoteProcessorProviderProps = {
  children: React.ReactNode
}

export function NoteProcessorProvider({
  children,
}: NoteProcessorProviderProps) {
  const [queue, setQueue] = useState<Queue>({
    batches: [],
    isProcessing: false,
  })
  const { showNotification } = useNotification()
  const createNoteMutation = useCreateNote()
  const saveNoteMutation = useSaveNote()
  const { data: privateExtension } = usePrivateNotesExtension()

  const user = useAuthenticatedUser()
  const queryClient = useQueryClient()
  const cachedNotesQueryClient: QueryClient = useQueryClient({
    context: cachedNotesContext,
  })
  const timeoutReference = useRef<number | undefined>(undefined)

  const updateBatch = useCallback(
    (batchId: string, updates: Partial<Batch>) => {
      setQueue((previous) => {
        const batchIndex = previous.batches.findIndex((b) => b.id === batchId)
        if (batchIndex === -1) return previous

        const updatedBatches = [...previous.batches]
        updatedBatches[batchIndex] = {
          ...updatedBatches[batchIndex],
          ...updates,
        }

        return {
          ...previous,
          batches: updatedBatches,
        }
      })
    },
    []
  )

  const updateOperation = useCallback(
    (batchId: string, operationId: string, updates: Partial<Operation>) => {
      setQueue((previous) => {
        const batchIndex = previous.batches.findIndex((b) => b.id === batchId)
        if (batchIndex === -1) return previous

        const batch = previous.batches[batchIndex]
        const opIndex = batch.operations.findIndex(
          (op) => op.id === operationId
        )
        if (opIndex === -1) return previous

        const updatedOps = [...batch.operations]
        updatedOps[opIndex] = { ...updatedOps[opIndex], ...updates }

        const updatedBatches = [...previous.batches]
        updatedBatches[batchIndex] = {
          ...batch,
          operations: updatedOps,
        }

        return {
          ...previous,
          batches: updatedBatches,
        }
      })
    },
    []
  )

  const selectNextBatch = useCallback(() => {
    setQueue((previous) => {
      if (previous.isProcessing) return previous
      const nextBatch = previous.batches.find(
        (batch) => batch.status === 'queued'
      )
      if (!nextBatch) return previous

      const freshBatch = {
        ...nextBatch,
        status: 'processing' as const,
        notificationSent: false,
      }
      const updatedBatches = previous.batches.map((batch) =>
        batch.id === nextBatch.id ? freshBatch : batch
      )
      return {
        batches: updatedBatches,
        isProcessing: true,
      }
    })
  }, [])

  // Helper function to defer batch processing to next tick to avoid state conflicts
  const deferredSelectNextBatch = useCallback(() => {
    if (!queue.isProcessing) {
      setTimeout(() => {
        selectNextBatch()
      }, 0)
    }
  }, [queue.isProcessing, selectNextBatch])

  const processAddOperation = useCallback(
    async (operationId: string, operation: Operation) => {
      const batch = queue.batches.find((b) =>
        b.operations.some((op) => op.id === operationId)
      )
      if (!batch || !operation.content || !privateExtension) {
        if (batch)
          updateOperation(batch.id, operationId, {
            status: 'error',
            errorMessage: 'Missing data for add op',
          })
        return
      }

      const contentToAdd = operation.content.trim()
      if (!contentToAdd) {
        updateOperation(batch.id, operationId, { status: 'completed' })
        return
      }

      // Get the teamspace ID from the batch
      // eslint-disable-next-line unicorn/no-null -- supabase needs null
      const teamspaceId = batch.teamspaceId ?? null

      // Use the appropriate note type based on teamspace context
      const noteType = teamspaceId
        ? NoteType.TEAM_SPACE_CALENDAR_NOTE
        : NoteType.CALENDAR_NOTE

      const extension = getNoteExtension(noteType, privateExtension, true)

      const noteConfig: useNoteConfig = {
        noteType,
        filename: `${operation.dateString ?? 'unknown'}.${extension}`,
        parent: teamspaceId, // Set parent to teamspace ID if in a teamspace
      }
      const queryKey = noteQueryKey(noteConfig)

      try {
        const noteData = await queryClient.fetchQuery<Note | undefined>({
          // eslint-disable-next-line @tanstack/query/exhaustive-deps -- user is only needed for looking up the cached note
          queryKey,
          queryFn: () =>
            fetchNote(queryClient, cachedNotesQueryClient, user, noteConfig),
          staleTime: 0,
        })

        if (noteData?.recordName) {
          const existingContent = noteData.content ?? ''
          const contentLines = existingContent.split('\n')

          if (contentLines.includes(contentToAdd)) {
            updateOperation(batch.id, operationId, { status: 'completed' })
            return
          }

          let updatedContent: string

          if (existingContent) {
            // Check for frontmatter
            const { bodyStartLineIndex } = readNoteTitleFromFrontmatter({
              content: existingContent,
            } as Note)

            if (bodyStartLineIndex && bodyStartLineIndex > 0) {
              // Has frontmatter - insert content after it
              const lines = existingContent.split('\n')
              const frontmatterLines = lines.slice(0, bodyStartLineIndex)
              const bodyLines = lines.slice(bodyStartLineIndex)

              updatedContent = [
                ...frontmatterLines,
                contentToAdd,
                ...bodyLines,
              ].join('\n')
            } else {
              // No frontmatter - prepend as before
              updatedContent = `${contentToAdd}\n${existingContent}`
            }
          } else {
            updatedContent = contentToAdd
          }

          saveNoteMutation.mutate({
            recordName: noteData.recordName,
            noteType: noteConfig.noteType,
            content: updatedContent,
            filename: noteConfig.filename,
            parent: noteConfig.parent,
            // Don't add attachments to upload, they will be ignored. We don't need to pass attachments if we are just editing text.
            // attachments: Array.isArray(noteData.attachments)
            //   ? noteData.attachments.map(String)
            //   : [],
          })
        } else {
          createNoteMutation.mutate(
            {
              recordName: uuid(),
              noteType: noteConfig.noteType,
              filename: operation.dateString,
              content: contentToAdd,
              parent: teamspaceId, // Include parent for teamspace notes
            },
            {
              onError: (createError) => {
                updateOperation(batch.id, operationId, {
                  status: 'error',
                  errorMessage: createError.message,
                })
                updateBatch(batch.id, { status: 'error' })
              },
            }
          )
        }

        updateOperation(batch.id, operationId, { status: 'completed' })
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown fetch/save error'
        trackError(
          'noteProcessor',
          `Error processing ADD for ${noteConfig.filename}`,
          { error: error instanceof Error ? error.message : String(error) }
        )
        updateOperation(batch.id, operationId, {
          status: 'error',
          errorMessage,
        })
        updateBatch(batch.id, { status: 'error' })
      }
    },
    [
      queue.batches,
      saveNoteMutation,
      createNoteMutation,
      updateOperation,
      updateBatch,
      queryClient,
      cachedNotesQueryClient,
      user,
      privateExtension,
    ]
  )

  const processCreateFolderOperation = useCallback(
    (operationId: string, operation: Operation) => {
      const batch = queue.batches.find((b) =>
        b.operations.some((op) => op.id === operationId)
      )
      if (!batch || !operation.filename || !operation.noteType) {
        if (batch)
          updateOperation(batch.id, operationId, {
            status: 'error',
            errorMessage: 'Missing data for createFolder op',
          })
        return
      }

      try {
        createNoteMutation.mutate(
          {
            filename: operation.filename,
            title: operation.title ?? operation.filename,
            noteType: operation.noteType,
            // eslint-disable-next-line unicorn/no-null -- supabase needs null
            parent: operation.parent ?? null,
            isDir: true,
            recordName: uuid(),
          },
          {
            onSuccess: () => {
              updateOperation(batch.id, operationId, { status: 'completed' })
            },
            onError: (createError) => {
              // Check if the error is due to duplicate folder
              if (
                createError.message.toLowerCase().includes('already exists') ||
                createError.message.toLowerCase().includes('duplicate')
              ) {
                updateOperation(batch.id, operationId, { status: 'completed' })
              } else {
                updateOperation(batch.id, operationId, {
                  status: 'error',
                  errorMessage: createError.message,
                })
                updateBatch(batch.id, { status: 'error' })
              }
            },
          }
        )
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown createFolder error'
        trackError(
          'noteProcessor',
          `Error processing createFolder for ${operation.filename}`,
          { error: error instanceof Error ? error.message : String(error) }
        )
        updateOperation(batch.id, operationId, {
          status: 'error',
          errorMessage,
        })
        updateBatch(batch.id, { status: 'error' })
      }
    },
    [queue.batches, createNoteMutation, updateOperation, updateBatch]
  )

  const processRemoveOperation = useCallback(
    async (operationId: string, operation: Operation) => {
      const batch = queue.batches.find((b) =>
        b.operations.some((op) => op.id === operationId)
      )
      if (!batch || !operation.pattern || !privateExtension) {
        if (batch)
          updateOperation(batch.id, operationId, {
            status: 'error',
            errorMessage: 'Missing data for remove op',
          })
        return
      }

      // Get the teamspace ID from the batch
      // eslint-disable-next-line unicorn/no-null -- supabase needs null
      const teamspaceId = batch.teamspaceId ?? null

      // Use the appropriate note type based on teamspace context
      const noteType = teamspaceId
        ? NoteType.TEAM_SPACE_CALENDAR_NOTE
        : NoteType.CALENDAR_NOTE

      const extension = getNoteExtension(noteType, privateExtension, true)

      const noteConfig: useNoteConfig = {
        noteType,
        filename: `${operation.dateString ?? 'untitled'}.${extension}`,
        parent: teamspaceId, // Set parent to teamspace ID if in a teamspace
      }
      const queryKey = noteQueryKey(noteConfig)

      try {
        const noteData = await queryClient.fetchQuery<Note | undefined>({
          // eslint-disable-next-line @tanstack/query/exhaustive-deps -- user is only needed for looking up the cached note
          queryKey,
          queryFn: () =>
            fetchNote(queryClient, cachedNotesQueryClient, user, noteConfig),
          staleTime: 0,
        })

        const existingContent = noteData?.content ?? ''

        if (!existingContent) {
          updateOperation(batch.id, operationId, { status: 'completed' })
          return
        }

        const patterns: string[] = operation.pattern
          ? Array.isArray(operation.pattern)
            ? operation.pattern
            : [String(operation.pattern)]
          : []

        const contentLines = existingContent.split('\n')
        const resultLines: string[] = []
        for (const line of contentLines) {
          let shouldRemove = false
          for (const pattern of patterns) {
            const normalizedLine = line.trim().replaceAll(/\s+/g, ' ')
            const normalizedPattern = String(pattern)
              .trim()
              .replaceAll(/\s+/g, ' ')
            if (normalizedLine === normalizedPattern) {
              shouldRemove = true
              break
            }
          }
          if (!shouldRemove) {
            resultLines.push(line)
          }
        }
        const updatedContent = resultLines.join('\n')

        if (updatedContent === existingContent) {
          // No changes needed
        } else {
          if (!noteData?.recordName) {
            throw new Error(
              `Missing recordName for note ${noteConfig.filename} during save`
            )
          }

          saveNoteMutation.mutate({
            recordName: noteData.recordName,
            noteType: noteConfig.noteType,
            content: updatedContent,
            filename: noteConfig.filename,
            parent: noteConfig.parent,
            // Don't add attachments to upload, they will be ignored. We don't need to pass attachments if we are just editing text.
            // attachments: Array.isArray(noteData.attachments)
            //   ? noteData.attachments.map(String)
            //   : [],
          })
        }

        updateOperation(batch.id, operationId, { status: 'completed' })
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown fetch/save error'
        trackError(
          'noteProcessor',
          `Error processing REMOVE for ${noteConfig.filename}`,
          { error: error instanceof Error ? error.message : String(error) }
        )
        updateOperation(batch.id, operationId, {
          status: 'error',
          errorMessage,
        })
        updateBatch(batch.id, { status: 'error' })
      }
    },
    [
      queue.batches,
      saveNoteMutation,
      updateOperation,
      updateBatch,
      queryClient,
      cachedNotesQueryClient,
      user,
      privateExtension,
    ]
  )

  /**
   * Main batch processing effect - orchestrates the queue processing workflow
   *
   * This effect is responsible for:
   * 1. Finding and processing the next pending operation in the current batch
   * 2. Marking batches as completed when all operations are done
   * 3. Starting the next batch when the current one finishes
   *
   * The queue system processes one batch at a time, and within each batch,
   * processes one operation at a time sequentially.
   */
  useEffect(() => {
    // Find the batch that's currently being processed (if any)
    const processingBatch = queue.batches.find((b) => b.status === 'processing')

    if (processingBatch) {
      // Check if there's an operation currently being processed
      const currentProcessingOp = processingBatch.operations.find(
        (op) => op.status === 'processing'
      )
      // Find the next operation that's waiting to be processed
      const nextPendingOp = processingBatch.operations.find(
        (op) => op.status === 'pending'
      )

      // Case 1: No operation is currently processing, but we have a pending one
      // => Start processing the next pending operation
      if (currentProcessingOp === undefined && nextPendingOp !== undefined) {
        // Mark the operation as processing
        updateOperation(processingBatch.id, nextPendingOp.id, {
          status: 'processing',
        })
        // Use setTimeout to avoid blocking the UI thread
        setTimeout(() => {
          // Process the operation based on its type
          switch (nextPendingOp.type) {
            case 'add': {
              void processAddOperation(nextPendingOp.id, nextPendingOp)
              break
            }
            case 'remove': {
              void processRemoveOperation(nextPendingOp.id, nextPendingOp)
              break
            }
            case 'createFolder': {
              processCreateFolderOperation(nextPendingOp.id, nextPendingOp)
              break
            }
            // No default
          }
        }, 0)
      }
      // Case 2: No operation is currently processing, no pending operations,
      // and all operations are either completed or have errors
      // => The batch is finished, mark it as completed or error
      else if (
        currentProcessingOp === undefined &&
        nextPendingOp === undefined &&
        processingBatch.operations.every(
          (op) => op.status === 'completed' || op.status === 'error'
        )
      ) {
        // Check if any operations had errors
        const hasErrors = processingBatch.operations.some(
          (op) => op.status === 'error'
        )
        // Update the batch status based on whether there were errors
        updateBatch(processingBatch.id, {
          status: hasErrors ? 'error' : 'completed',
        })
        // Mark the queue as not processing anymore
        setQueue((previous) => ({ ...previous, isProcessing: false }))
        // Clear any timeout that might have been set for this batch
        if (timeoutReference.current) {
          window.clearTimeout(timeoutReference.current)
          timeoutReference.current = undefined
        }
        // Try to start processing the next batch in the queue
        selectNextBatch()
      }
    }
    // Case 3: No batch is currently processing, but there are queued batches
    // => Start processing the next batch
    else if (
      !queue.isProcessing &&
      queue.batches.some((b) => b.status === 'queued')
    ) {
      selectNextBatch()
    }
  }, [
    queue,
    updateOperation,
    updateBatch,
    selectNextBatch,
    processAddOperation,
    processRemoveOperation,
    processCreateFolderOperation,
  ])

  /**
   * Batch timeout safety mechanism
   *
   * This effect sets up a timeout for each processing batch to prevent batches
   * from getting stuck in the processing state indefinitely.
   *
   * If a batch takes longer than 10 seconds to process, it will:
   * 1. Mark all pending/processing operations as errors with a 'Timeout' message
   * 2. Mark the batch as completed or error based on operation statuses
   * 3. Reset the queue's processing state
   * 4. Try to start the next batch
   *
   * This ensures the queue system can recover from unexpected errors or
   * operations that take too long to complete.
   */
  useEffect(() => {
    // Only set up a timeout if the queue is actively processing
    if (queue.isProcessing) {
      // Find the batch that's currently being processed
      const processingBatch = queue.batches.find(
        (b) => b.status === 'processing'
      )
      if (!processingBatch) return

      // Clear any existing timeout to avoid multiple timeouts
      if (timeoutReference.current) {
        window.clearTimeout(timeoutReference.current)
      }

      // Set a new timeout for the current processing batch
      timeoutReference.current = window.setTimeout(() => {
        // Log the timeout error for tracking
        trackError('noteProcessor', `Batch ${processingBatch.id} timed out`, {
          timeout: BATCH_TIMEOUT,
        })

        // Update the queue state to mark timed-out operations as errors
        setQueue((previous) => {
          const batch = previous.batches.find(
            (b) => b.id === processingBatch.id
          )
          if (!batch || batch.status !== 'processing') return previous

          // Mark all pending or processing operations as errors
          const updatedOps = batch.operations.map((op) => {
            if (op.status === 'pending' || op.status === 'processing') {
              return {
                ...op,
                status: 'error' as OperationStatus,
                errorMessage: 'Timeout',
              }
            }
            return op
          })

          // Check if any operations had errors
          const hasErrors = updatedOps.some((op) => op.status === 'error')

          // Update the batch status in the queue
          const updatedBatches = previous.batches.map((b) =>
            b.id === processingBatch.id
              ? {
                  ...b,
                  operations: updatedOps,
                  status: hasErrors
                    ? ('error' as BatchStatus)
                    : ('completed' as BatchStatus),
                }
              : b
          )

          // Return the updated queue state
          return {
            batches: updatedBatches,
            isProcessing: false,
          }
        })

        // Try to start the next batch after a small delay
        setTimeout(() => {
          selectNextBatch()
        }, 0)
      }, BATCH_TIMEOUT) // 10 second timeout

      // Cleanup function to clear the timeout when the effect is cleaned up
      return () => {
        if (timeoutReference.current) {
          window.clearTimeout(timeoutReference.current)
          timeoutReference.current = undefined
        }
      }
    }
  }, [queue.isProcessing, queue.batches, selectNextBatch])

  /**
   * Notification effect for completed batches
   *
   * This effect monitors the queue for completed or error batches and shows
   * appropriate notifications to the user. It ensures each batch only triggers
   * a notification once by tracking the 'notificationSent' flag.
   *
   * For error batches, it shows an error notification with details about what failed.
   * For successfully completed batches, it shows a success notification.
   *
   * This provides important feedback to users about background operations that
   * may have been initiated from different parts of the application.
   */
  useEffect(() => {
    // Find batches that have completed (success or error) but haven't shown a notification yet
    const completedBatches = queue.batches.filter(
      (batch) =>
        (batch.status === 'completed' || batch.status === 'error') &&
        !batch.notificationSent
    )

    for (const batch of completedBatches) {
      // Mark the batch as having sent a notification to avoid duplicate notifications
      updateBatch(batch.id, { notificationSent: true })

      // Get a user-friendly description of what the batch was doing
      const batchDescription = batch.description ?? 'Background processing'
      // console.debug(
      //   '🔔 NoteProcessorProvider: Showing notification for batch:',
      //   {
      //     batchId: batch.id,
      //     description: batch.description,
      //     batchDescription,
      //     operations: batch.operations.map((op) => ({
      //       type: op.type,
      //       status: op.status,
      //     })),
      //   }
      // )

      // Check if any operations in the batch had errors
      const hasErrors = batch.operations.some((op) => op.status === 'error')
      if (hasErrors) {
        // Find the first operation with an error to get its error message
        const errorOp = batch.operations.find((op) => op.status === 'error')
        const errorMessage = errorOp?.errorMessage ?? 'Unknown error'

        // Track the error for analytics
        trackError('noteProcessor', `Error processing operations`, {
          batchId: batch.id,
          errorMessage: errorMessage,
        })

        // Show an error notification to the user
        showNotification({
          title: 'Error',
          message: `Failed to process '${batchDescription}': ${errorMessage}`,
          icon: errorIcon,
        })
      } else if (
        batch.status === 'completed' &&
        batch.operations.length > 0 &&
        batch.description
      ) {
        // Show a success notification for completed batches with operations AND description
        // Skip notification if no description to avoid overriding other notifications
        showNotification({
          title: batchDescription,
          message: 'Notes successfully processed',
        })
      }
    }
  }, [queue.batches, updateBatch, showNotification])

  const addContent = useCallback(
    (
      items: AddContentInput[],
      description?: string,
      batchId?: string,
      teamspaceId?: string
    ): string | undefined => {
      if (items.length === 0) return undefined

      // Store the teamspace ID in the batch for later use
      const operations = createOperations(items)
      const batch = createBatch(operations, description, batchId, teamspaceId)

      setQueue((previous) => ({
        ...previous,
        batches: [...previous.batches, batch],
      }))

      deferredSelectNextBatch()

      return batch.id
    },
    [deferredSelectNextBatch]
  )

  const removeContent = useCallback(
    (
      items: RemoveContentInput[],
      description?: string,
      teamspaceId?: string
    ): string | undefined => {
      if (items.length === 0) return undefined

      const operations = createOperations(items)
      const batch = createBatch(operations, description, undefined, teamspaceId)

      setQueue((previous) => ({
        ...previous,
        batches: [...previous.batches, batch],
      }))

      deferredSelectNextBatch()

      return batch.id
    },
    [deferredSelectNextBatch]
  )

  const createFolder = useCallback(
    (items: CreateFolderInput[], description?: string): string | undefined => {
      if (items.length === 0) return undefined

      const operations = createFolderOperations(items)
      const batch = createBatch(operations, description)

      setQueue((previous) => ({
        ...previous,
        batches: [...previous.batches, batch],
      }))

      deferredSelectNextBatch()

      return batch.id
    },
    [deferredSelectNextBatch]
  )

  const contextValue: NoteProcessorContextType = {
    addContent,
    removeContent,
    createFolder,
  }

  return (
    <NoteProcessorContext.Provider value={contextValue}>
      {children}
    </NoteProcessorContext.Provider>
  )
}
